// frontend/src/components/Templates/TemplateList.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Tooltip,
  Modal,
  Form,
  Select,
  InputNumber,
  Switch,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { templateApi } from '../../services/api';
import type { Template } from '../../types';
import { useIsOwner } from '../auth/RoleGuard';

const { Title, Text } = Typography;
const { Search } = Input;

/** Summarize a template in one short line (mirrors TemplatePicker). */
const summarize = (t: Template): string =>
  t.type === 'part'
    ? t.part_number || '—'
    : `${(t.payload as any).labor_hours ?? 0}h labor · ${((t.payload as any).parts || []).length} part(s)`;

const TemplateList: React.FC = () => {
  // WP-09 — only an owner decides what a technician is offered to quote.
  const isOwner = useIsOwner();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTemplates = useCallback(async (search: string) => {
    setLoading(true);
    try {
      const res = await templateApi.getAll({ search: search || undefined });
      setTemplates(res.data);
    } catch {
      message.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchTemplates('');
  }, [fetchTemplates]);

  // Debounced re-fetch on search change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchTemplates(searchTerm), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, fetchTemplates]);

  const openEdit = (t: Template) => {
    setEditingTemplate(t);
    const partPayload = t.type === 'part' ? (t.payload as any) : {};
    form.setFieldsValue({
      name: t.name,
      part_number: t.part_number ?? undefined,
      unit_price: partPayload.unit_price ?? undefined,
      availability: partPayload.availability ?? undefined,
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingTemplate(null);
    form.resetFields();
  };

  const handleDelete = async (id: number) => {
    try {
      await templateApi.delete(id);
      message.success('Template deleted');
      fetchTemplates(searchTerm);
    } catch {
      message.error('Failed to delete template');
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const patch: Partial<Template> = { name: values.name };
      if (editingTemplate.type === 'part') {
        patch.part_number = values.part_number ?? editingTemplate.part_number;
        patch.payload = {
          ...(editingTemplate.payload as any),
          unit_price: values.unit_price ?? (editingTemplate.payload as any).unit_price,
          availability: values.availability ?? (editingTemplate.payload as any).availability,
        };
      }
      await templateApi.update(editingTemplate.id, patch);
      message.success('Template updated');
      closeModal();
      fetchTemplates(searchTerm);
    } catch (e: any) {
      if (e?.errorFields) return; // form validation error — antd handles display
      message.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  /**
   * WP-09 — put a labor template into (or take it out of) the inspection quick
   * picker. The row is updated from the SERVER's response rather than flipped
   * optimistically: this decides what pricing a technician can put in front of
   * a customer, so the switch must show what was actually saved.
   */
  const handleQuickPicker = async (t: Template, eligible: boolean) => {
    setTogglingId(t.id);
    try {
      const res = await templateApi.setQuickPickerEligible(t.id, eligible);
      setTemplates((prev) => prev.map((x) => (x.id === t.id ? res.data : x)));
      message.success(
        eligible ? 'Added to the inspection quick picker' : 'Removed from the quick picker',
      );
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? 'Failed to update the quick picker');
    } finally {
      setTogglingId(null);
    }
  };

  const columns: ColumnsType<Template> = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'line' ? 'blue' : 'gold'}>
          {type === 'line' ? 'Line' : 'Part'}
        </Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Summary',
      key: 'summary',
      render: (_: any, record: Template) => (
        <Text type="secondary">{summarize(record)}</Text>
      ),
    },
    {
      title: 'Used',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 70,
      align: 'center' as const,
      render: (n: number) => <Text type="secondary">{n}</Text>,
    },
    {
      // Only labor templates are jobs, so a part template shows a dash rather
      // than a switch that would be refused.
      title: 'Quick picker',
      key: 'quick_picker',
      width: 110,
      align: 'center' as const,
      render: (_: any, record: Template) =>
        record.type !== 'line' ? (
          <Text type="secondary">—</Text>
        ) : (
          <Tooltip
            title={
              isOwner
                ? 'Offer this job when adding an inspection finding to an estimate'
                : 'Only an owner can change this'
            }
          >
            <Switch
              size="small"
              checked={!!record.quick_picker_eligible}
              loading={togglingId === record.id}
              disabled={!isOwner}
              onChange={(v) => handleQuickPicker(record, v)}
              aria-label={`Quick picker for ${record.name}`}
            />
          </Tooltip>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: Template) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Template"
            description="Are you sure you want to delete this template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <Title level={3} style={{ margin: 0 }}>
                Templates
              </Title>
            </div>
          </div>

          <Search
            placeholder="Search by name or part number…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={(v) => fetchTemplates(v)}
            allowClear
            style={{ maxWidth: 340 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Template"
        open={modalVisible}
        onOk={handleSave}
        onCancel={closeModal}
        okText="Save"
        confirmLoading={saving}
        destroyOnHidden
        width={480}
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Template name" />
          </Form.Item>

          {editingTemplate?.type === 'part' && (
            <>
              <Form.Item name="part_number" label="Part Number">
                <Input placeholder="e.g., OF-12345" />
              </Form.Item>
              <Form.Item name="unit_price" label="Unit Price ($)">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="availability" label="Availability">
                <Select
                  options={[
                    { label: 'In Stock', value: 'in_stock' },
                    { label: 'Order Required', value: 'order_required' },
                    { label: 'Backordered', value: 'backordered' },
                    { label: 'Discontinued', value: 'discontinued' },
                  ]}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default TemplateList;
